export type FloorPlanSettings = {
  originXMm: number;
  originYMm: number;
  widthMm: number;
  heightMm: number;
  tileSizeMm: number;
};

export const DEFAULT_FLOOR_PLAN: FloorPlanSettings = {
  originXMm: 0,
  originYMm: 0,
  widthMm: 60000,
  heightMm: 60000,
  tileSizeMm: 600,
};

export function validateFloorPlan(settings: FloorPlanSettings): void {
  const values = [settings.originXMm, settings.originYMm, settings.widthMm, settings.heightMm, settings.tileSizeMm];
  if (values.some(value => !Number.isInteger(value))) throw new Error('FloorPlan geometry must use integer millimetres');
  if (settings.widthMm <= 0 || settings.heightMm <= 0 || settings.tileSizeMm <= 0) {
    throw new Error('FloorPlan bounds and tile size must be positive');
  }
}

export function roundMetersToMillimetres(metres: number): number {
  return Math.round(metres * 1000);
}

export function millimetresToMetres(mm: number): number {
  return mm / 1000;
}

export function snapMillimetres(valueMm: number, originMm: number, tileSizeMm: number): number {
  if (!Number.isInteger(tileSizeMm) || tileSizeMm <= 0) throw new Error('Tile size must be positive');
  return originMm + Math.round((valueMm - originMm) / tileSizeMm) * tileSizeMm;
}

export function snapMetresToFloorGrid(metres: number, axis: 'x' | 'y', settings: FloorPlanSettings): number {
  validateFloorPlan(settings);
  const origin = axis === 'x' ? settings.originXMm : settings.originYMm;
  return millimetresToMetres(snapMillimetres(roundMetersToMillimetres(metres), origin, settings.tileSizeMm));
}

export function previewBoundsExpansion(current: FloorPlanSettings, requested: Pick<FloorPlanSettings, 'originXMm' | 'originYMm' | 'widthMm' | 'heightMm'>) {
  validateFloorPlan(current);
  const next = { ...current, ...requested };
  validateFloorPlan(next);
  const currentMaxX = current.originXMm + current.widthMm;
  const currentMaxY = current.originYMm + current.heightMm;
  const nextMaxX = next.originXMm + next.widthMm;
  const nextMaxY = next.originYMm + next.heightMm;
  return {
    before: { minXmm: current.originXMm, minYmm: current.originYMm, maxXmm: currentMaxX, maxYmm: currentMaxY },
    after: { minXmm: next.originXMm, minYmm: next.originYMm, maxXmm: nextMaxX, maxYmm: nextMaxY },
    expanded: next.originXMm <= current.originXMm && next.originYMm <= current.originYMm && nextMaxX >= currentMaxX && nextMaxY >= currentMaxY,
    preservesGeometry: true,
  };
}
